// src/components/VoiceChat.tsx
import React, { useState, useEffect, useRef } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import {useSpeechSynthesis} from 'react-speech-kit';
import axios from 'axios';
import './VoiceChat.css';

type Message = {
  role: 'user' | 'assistant' | 'system';
  content: string;
};

type OpenAIResponse = {
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
  }>;
};

type VoiceChatProps = {  
    onFinish?: () => void;
    systemMessage?: string;
    firstMessage?: string;
};

const VoiceChat: React.FC<VoiceChatProps> = (props: VoiceChatProps) => {
  // Speech Recognition Hook
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  // Speech Synthesis Hook
  const { speak, speaking, supported: speechSynthesisSupported, voices } = useSpeechSynthesis();

  // Component State
  const [conversation, setConversation] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);

  // Refs for managing speech
  const lastTranscriptRef = useRef('');
  const processingRef = useRef(false);

    // Start listening
  const startListening = () => {
    setIsListening(true);
    setError(null);
    SpeechRecognition.startListening({
      continuous: true,
      language: 'en-US'
    });
  };

  // Stop listening
  const stopListening = () => {
    setIsListening(false);
    SpeechRecognition.stopListening();
  };

  // Clear conversation
  const clearConversation = () => {
    setConversation([]);
    resetTranscript();
    setError(null);
  };

  // Initialize voice selection
  useEffect(() => {
    if (voices.length > 0 && !selectedVoice) {
      const englishVoice = voices.find((voice: { lang: string | string[]; }) => voice.lang.includes('en')) || voices[0];
      setSelectedVoice(englishVoice);
    }
  }, [voices, selectedVoice]);

  // Handle speech recognition results
  useEffect(() => {
    if (transcript && transcript !== lastTranscriptRef.current && !processingRef.current) {
      const timer = setTimeout(() => {
        if (transcript === lastTranscriptRef.current) {
          handleSpeechInput(transcript);
        }
      }, 1500);

      lastTranscriptRef.current = transcript;
      return () => clearTimeout(timer);
    }
    if (listening && speaking) {
      setIsListening(false);
      stopListening();
    } else if (!listening && !speaking) {
      try{
        startListening();
        setIsListening(true);
      }catch (error) {
        console.error('Error in speech recognition:', error);
      }
      
    }

  }, [transcript, listening, speaking]);

  // Check browser support
  if (!browserSupportsSpeechRecognition) {
    return (
      <div className="voice-chat-container">
        <div className="error-message">
          Browser doesn't support speech recognition. Please use Chrome or Edge.
        </div>
      </div>
    );
  }

  if (!speechSynthesisSupported) {
    return (
      <div className="voice-chat-container">
        <div className="error-message">
          Browser doesn't support speech synthesis.
        </div>
      </div>
    );
  }

  // Handle speech input and send to OpenAI
  const handleSpeechInput = async (spokenText: string) => {
    if (!spokenText.trim() || processingRef.current) return;

    processingRef.current = true;
    setIsProcessing(true);
    setError(null);

    try {
      // Add user message to conversation
      
      const userMessage: Message = { role: 'user', content: spokenText };
      const systemMessage: Message = { role: 'system', content:  props.systemMessage || 'You are a helpful traveling AI assistant. You would like to gather information from the user in a very friendly way, by helping her to discover what she likes to do when traveling. You need to fill out the HATPro schema definition to fill out a traveler profile. You are helping the user search for traveling options and gather information in the same time. Keep the communication under 15 seconds. When the user says thank you, you will say that you compleated users traveling profile and she can follow the next steps to download it.' };
      const updatedConversation = [systemMessage, ...conversation, userMessage];
      setConversation(updatedConversation);

      // Send to OpenAI API
      const response = await axios.post<OpenAIResponse>(
        process.env.REACT_APP_OPENAI_API_URL!,
        {
          model: 'gpt-3.5-turbo',
          messages: updatedConversation,
          max_tokens: 150,
          temperature: 0.7
        },
        {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const aiResponse = response.data.choices[0].message.content;
      const assistantMessage: Message = { role: 'assistant', content: aiResponse };
      
      // Update conversation with AI response
      setConversation(prev => [...prev, assistantMessage]);

      // Speak the AI response
      if (selectedVoice) {
        speak({
          text: aiResponse,
          voice: selectedVoice
        });
        if (transcript.toLowerCase().indexOf('thank you') > -1 || transcript.toLowerCase().indexOf('thanks') > -1) {
            props.onFinish?.();
        }
      }

    } catch (error) {
      console.error('Error communicating with OpenAI:', error);
      setError('Failed to get AI response. Please try again.');
    } finally {
      setIsProcessing(false);
      processingRef.current = false;
      resetTranscript();
    }
  };



  return (
    <div className="voice-chat-container">
      <div className="header">
        <h1>Traveling AI Assistant</h1>
        <p>Click the microphone to start talking with our traveling AI Assistant</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="voice-controls">
        <button
          className={`mic-button ${isListening ? 'listening' : ''} ${isProcessing ? 'processing' : ''}`}
          onClick={isListening ? stopListening : startListening}
          disabled={isProcessing || speaking}
        >
          {isProcessing ? '🤖' : isListening ? '🔴' : '🎤'}
        </button>
        
        <div className="status">
          {isProcessing && <span className="processing">AI is thinking...</span>}
          {speaking && <span className="speaking">AI is speaking...</span>}
          {isListening && <span className="listening">Listening...</span>}
          {!isListening && !isProcessing && !speaking && <span className="idle">Ready</span>}
        </div>
      </div>

      {transcript && (
        <div className="current-transcript">
          <strong>You said:</strong> {transcript}
        </div>
      )}

      <div className="voice-settings d-none">
        <label htmlFor="voice-select">AI Voice:</label>
        <select
          id="voice-select"
          value={selectedVoice?.name || ''}
          onChange={(e) => {
            const voice = voices.find((v: { name: string; }) => v.name === e.target.value);
            if (voice) setSelectedVoice(voice);
          }}
        >
          {voices
            .filter((voice: { lang: string | string[]; }) => voice.lang.includes('en'))
            .map((voice: { lang: string | string[]; name: string;}) => (
              <option key={voice.name} value={voice.name}>
                {voice.name} ({voice.lang})
              </option>
            ))}
        </select>
      </div>

      <div className="conversation d-none">
        <div className="conversation-header">
          <h3>Conversation</h3>
          {conversation.length > 0 && (
            <button className="clear-button" onClick={clearConversation}>
              Clear
            </button>
          )}
        </div>
        
        <div className="messages d-none">
          {conversation.map((message, index) => (
            <div
              key={index}
              className={`message ${message.role === 'user' ? 'user-message' : 'ai-message'}`}
            >
              <div className="message-role">
                {message.role === 'user' ? '👤 You' : '🤖 AI'}
              </div>
              <div className="message-content">{message.content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VoiceChat;
